"use client";

import { useEffect, useRef, useState } from "react";
import { io, Socket } from "socket.io-client";
import { useAuth } from "@clerk/clerk-react";
import { getPublicRuntimeConfig } from "@/lib/config/public-runtime-config";

export const useSocket = () => {
  const socketRef = useRef<Socket | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const { getToken } = useAuth();
  const { websocketUrl } = getPublicRuntimeConfig();

  useEffect(() => {
    const initSocket = async () => {
      // Clean up any existing socket first
      if (socketRef.current) {
        socketRef.current.removeAllListeners();
        socketRef.current.disconnect();
        socketRef.current = null;
        setIsConnected(false);
      }

      // Try to get auth token (won't throw if not authenticated)
      let token: string | null = null;
      try {
        token = await getToken();
      } catch {
        // User is not authenticated, which is fine
        token = null;
      }

      socketRef.current = io(websocketUrl, {
        transports: ["websocket"],
        upgrade: false,
        auth: token ? { token } : undefined,
        reconnection: true,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 5000,
        reconnectionAttempts: 5,
      });

      socketRef.current.on("connect", () => {
        setIsConnected(true);
        // Note: Don't emit getInitialData here - let consumers emit after attaching listeners
      });

      socketRef.current.on("reconnect", () => {
        setIsConnected(true);
        // Note: Don't emit getInitialData here - let consumers emit after attaching listeners
      });

      socketRef.current.on("connect_error", (err) => {
        console.error("WebSocket connection error:", err.message);
        setIsConnected(false);
      });

      socketRef.current.on("disconnect", () => {
        setIsConnected(false);
        // If disconnect was not intentional, the socket will auto-reconnect
      });
    };

    void initSocket();

    return () => {
      if (socketRef.current) {
        socketRef.current.removeAllListeners();
        socketRef.current.disconnect();
        socketRef.current = null;
        setIsConnected(false);
      }
    };
  }, [getToken, websocketUrl]);

  return { socketRef, isConnected };
};
